import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type COLOR, COLORS } from '../../../types/Colors';
import type { DropdownOption } from '../../../types/Dropdown';
import { DROPDOWN_DIRECTION } from '../types';

interface UseDropdownMenuProps<T> {
    options: DropdownOption<T>[];
    value?: T | T[];
    multiple: boolean;
    color?: COLOR;
    placeholder: string;
    keepPlaceholder: boolean;
    formatSelectedOptions?: (labels: ReactNode[]) => ReactNode;
    controlledOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    onChange?: (value: T | T[]) => void;
    onSearch?: (searchTerm: string) => void;
    keepInitialPanelWidth: boolean;
    closeOnScroll: boolean;
    maxHeight: number;
}

interface UseDropdownMenuReturn<T> {
    isOpen: boolean;
    searchTerm: string;
    panelPosition: { x: number; y: number } | null;
    initialPanelWidth: number | null;
    triggerWidth: number | null;
    containerRef: RefObject<HTMLDivElement | null>;
    triggerRef: RefObject<HTMLElement | null>;
    panelRef: RefObject<HTMLDivElement | null>;
    effectiveColor: COLOR;
    filteredOptions: DropdownOption<T>[];
    displayValue: ReactNode;
    handleToggle: () => void;
    handleSelect: (optionValue: T) => void;
    handleSearchChange: (term: string) => void;
    isSelected: (optionValue: T) => boolean;
    setTriggerRef: (el: HTMLElement | null) => void;
}

export const useDropdownMenu = <T extends string | number>({
    closeOnScroll,
    color,
    controlledOpen,
    formatSelectedOptions,
    keepInitialPanelWidth,
    keepPlaceholder,
    maxHeight,
    multiple,
    onChange,
    onOpenChange,
    onSearch,
    options,
    placeholder,
    value,
}: UseDropdownMenuProps<T>): UseDropdownMenuReturn<T> => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [panelPosition, setPanelPosition] = useState<{ x: number; y: number } | null>(null);
    const [initialPanelWidth, setInitialPanelWidth] = useState<number | null>(null);
    const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const effectiveColor = useMemo((): COLOR => {
        if (color) return color;

        if (multiple) {
            const selectedValues = Array.isArray(value) ? value : [];
            if (selectedValues.length > 0) {
                const firstSelectedOption = options.find((opt) => opt.value === selectedValues[0]);
                if (firstSelectedOption?.color) return firstSelectedOption.color;
            }
        } else if (value !== undefined) {
            const selectedOption = options.find((opt) => opt.value === value);
            if (selectedOption?.color) return selectedOption.color;
        }

        return COLORS.WHITE;
    }, [color, multiple, options, value]);

    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!isControlled) {
                setInternalOpen(newOpen);
            }
            onOpenChange?.(newOpen);
        },
        [isControlled, onOpenChange],
    );

    const calculatePosition = useCallback(() => {
        const targetElement = triggerRef.current || containerRef.current;
        if (!targetElement || !containerRef.current) {
            return;
        }

        const rect = targetElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const panelHeight = panelRef.current?.offsetHeight ?? maxHeight;

        const newPosition =
            spaceBelow < panelHeight && spaceAbove > spaceBelow ? DROPDOWN_DIRECTION.TOP : DROPDOWN_DIRECTION.BOTTOM;

        const x = rect.left;
        const estimatedHeight = panelRef.current?.offsetHeight ?? maxHeight;
        const y = newPosition === DROPDOWN_DIRECTION.BOTTOM ? rect.bottom + 2.6 : rect.top - estimatedHeight - 2.6;

        setPanelPosition({ x, y });
        setTriggerWidth(rect.width);
    }, [maxHeight]);

    const handleToggle = useCallback(() => {
        const willOpen = !isOpen;

        if (willOpen) {
            const targetElement = triggerRef.current || containerRef.current;
            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                setPanelPosition({ x: rect.left, y: rect.bottom + 2.6 });
            }
            calculatePosition();
        }

        handleOpenChange(willOpen);
    }, [isOpen, handleOpenChange, calculatePosition]);

    const handleSelect = useCallback(
        (optionValue: T) => {
            if (multiple) {
                const currentValues = Array.isArray(value) ? value : [];
                const newValues = currentValues.includes(optionValue)
                    ? currentValues.filter((v) => v !== optionValue)
                    : [...currentValues, optionValue];
                onChange?.(newValues as T | T[]);
            } else {
                onChange?.(optionValue);
                handleOpenChange(false);
            }
        },
        [multiple, value, onChange, handleOpenChange],
    );

    const handleSearchChange = useCallback(
        (term: string) => {
            setSearchTerm(term);
            onSearch?.(term);
        },
        [onSearch],
    );

    const filteredOptions = useMemo(() => {
        if (!searchTerm) {
            return options;
        }
        return options.filter((option) => {
            const labelStr = typeof option.label === 'string' ? option.label : String(option.label);
            return labelStr.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [options, searchTerm]);

    const isSelected = useCallback(
        (optionValue: T): boolean => {
            if (multiple) {
                return Array.isArray(value) && value.includes(optionValue);
            }
            return value === optionValue;
        },
        [multiple, value],
    );

    const displayValue = useMemo((): ReactNode => {
        if (keepPlaceholder) {
            return placeholder;
        }
        if (multiple) {
            const selected = Array.isArray(value) ? value : [];
            if (selected.length === 0) return placeholder;
            if (selected.length === 1) {
                const option = options.find((opt) => opt.value === selected[0]);
                return option?.label ?? placeholder;
            }
            const selectedLabels = selected
                .map((val) => options.find((opt) => opt.value === val)?.label)
                .filter((label): label is ReactNode => label !== undefined);
            return formatSelectedOptions ? formatSelectedOptions(selectedLabels) : `${selected.length} selected`;
        }
        if (value === undefined) return placeholder;
        const option = options.find((opt) => opt.value === value);
        return option?.label ?? placeholder;
    }, [keepPlaceholder, multiple, value, options, placeholder, formatSelectedOptions]);

    const setTriggerRef = useCallback((el: HTMLElement | null) => {
        triggerRef.current = el;
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setPanelPosition(null);
            setInitialPanelWidth(null);
            setTriggerWidth(null);
            triggerRef.current = null;
            return undefined;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                panelRef.current &&
                !panelRef.current.contains(target)
            ) {
                handleOpenChange(false);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculatePosition();
            if (keepInitialPanelWidth && panelRef.current && initialPanelWidth === null) {
                setInitialPanelWidth(panelRef.current.offsetWidth);
            }
        });

        const handleResize = () => calculatePosition();
        const handleScroll = () => {
            if (closeOnScroll) {
                handleOpenChange(false);
            } else {
                calculatePosition();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            cancelAnimationFrame(rafId);
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, handleOpenChange, calculatePosition, initialPanelWidth, keepInitialPanelWidth, closeOnScroll]);

    return {
        containerRef,
        displayValue,
        effectiveColor,
        filteredOptions,
        handleSearchChange,
        handleSelect,
        handleToggle,
        initialPanelWidth,
        isOpen,
        isSelected,
        panelPosition,
        panelRef,
        searchTerm,
        setTriggerRef,
        triggerRef,
        triggerWidth,
    };
};
